import { App, Button, Drawer, Space, Spin, Tag, Typography } from 'antd';
import { Save, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { http } from '../../api/client.js';
import type { PrivilegeListResponse } from '../../components/admin/RolePrivilegeEditor.js';
import {
  PlatformPermissionDrawerTabs,
  buildPlatformMenuTree,
} from './PlatformPermissionDrawerTabs.js';
import type {
  PlatformPermissionTabKey,
  PlatformRoleDetail,
  PlatformSitemapNodesResponse,
} from './platform-permission-types.js';
import { isPlatformRoleType } from './platform-permission-types.js';

function platformRoleTagColor(roleType: string) {
  if (roleType === 'system') return 'red';
  if (roleType === 'admin') return 'orange';
  if (roleType === 'user') return 'blue';
  return 'default';
}

export function PlatformPermissionDetailDrawer({
  roleId,
  onClose,
  onSaved,
}: {
  roleId: string | null;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { message } = App.useApp();
  const { t } = useTranslation('platformPermissions');
  const [detail, setDetail] = useState<PlatformRoleDetail | null>(null);
  const [sitemap, setSitemap] = useState<PlatformSitemapNodesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<PlatformPermissionTabKey>('general');
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [checkedAdminIds, setCheckedAdminIds] = useState<string[]>([]);
  const [privilegeData, setPrivilegeData] = useState<PrivilegeListResponse | null>(null);
  const [privilegeChanges, setPrivilegeChanges] = useState<Map<string, number>>(new Map());
  const [privilegeQ, setPrivilegeQ] = useState('');
  const [privilegePage, setPrivilegePage] = useState(1);
  const [privilegePageSize, setPrivilegePageSize] = useState(15);
  const [privilegeLoading, setPrivilegeLoading] = useState(false);

  const loadDetail = useCallback(
    async (nextRoleId: string) => {
      setLoading(true);
      try {
        const [detailResponse, sitemapResponse] = await Promise.all([
          http.get<PlatformRoleDetail>(`/admin/platform-roles/${nextRoleId}`),
          http.get<PlatformSitemapNodesResponse>('/admin/platform-roles/sitemap-nodes'),
        ]);
        setDetail(detailResponse.data);
        setSitemap(sitemapResponse.data);
        setSelectedAppIds(detailResponse.data.apps.map((app) => app.appid));
      } catch {
        message.error(t('detailLoadFailed'));
      } finally {
        setLoading(false);
      }
    },
    [message, t],
  );

  useEffect(() => {
    if (!roleId) {
      setDetail(null);
      setSitemap(null);
      setActiveTab('general');
      return;
    }
    loadDetail(roleId);
  }, [loadDetail, roleId]);

  const adminApp = useMemo(
    () => sitemap?.apps.find((app) => app.name === 'admin') ?? null,
    [sitemap],
  );
  const adminItems = useMemo(
    () =>
      sitemap && adminApp
        ? sitemap.items
            .filter((item) => item.appid === adminApp.appid)
            .sort((a, b) => a.display_order - b.display_order)
        : [],
    [adminApp, sitemap],
  );
  const adminIds = useMemo(() => new Set(adminItems.map((item) => item.sitemap_id)), [adminItems]);
  const adminTree = useMemo(() => buildPlatformMenuTree(adminItems), [adminItems]);

  useEffect(() => {
    if (!detail) {
      setCheckedAdminIds([]);
      return;
    }
    setCheckedAdminIds(detail.visibleSitemapIds.filter((id) => adminIds.has(id)));
  }, [adminIds, detail]);

  useEffect(() => {
    setPrivilegeChanges(new Map());
    setPrivilegeQ('');
    setPrivilegePage(1);
    setPrivilegeData(null);
  }, [roleId]);

  const loadPrivileges = useCallback(async () => {
    if (!roleId) return;
    setPrivilegeLoading(true);
    const params = new URLSearchParams({
      page: String(privilegePage),
      pageSize: String(privilegePageSize),
      appName: 'admin',
    });
    if (privilegeQ.trim()) params.set('q', privilegeQ.trim());
    try {
      const response = await http.get<PrivilegeListResponse>(
        `/admin/platform-roles/${roleId}/privileges?${params.toString()}`,
      );
      setPrivilegeData(response.data);
    } catch {
      message.error(t('privilegesLoadFailed'));
    } finally {
      setPrivilegeLoading(false);
    }
  }, [message, privilegePage, privilegePageSize, privilegeQ, roleId, t]);

  useEffect(() => {
    if (activeTab !== 'privileges') return;
    void loadPrivileges();
  }, [activeTab, loadPrivileges]);

  const refreshDetail = async () => {
    if (!roleId) return;
    await loadDetail(roleId);
    onSaved?.();
  };

  const saveApps = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await http.put(`/admin/platform-roles/${detail.roleid}/apps`, { appIds: selectedAppIds });
      message.success(t('appsSaved'));
      await refreshDetail();
    } catch {
      message.error(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const saveAdminMenus = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await http.put(`/admin/platform-roles/${detail.roleid}/menu-permissions`, {
        sitemapIds: checkedAdminIds,
      });
      message.success(t('menusSaved'));
      await refreshDetail();
    } catch {
      message.error(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const savePlatformPrivileges = async () => {
    if (!detail || privilegeChanges.size === 0) return;
    setSaving(true);
    try {
      const changes = Array.from(privilegeChanges.entries()).map(([privilegeid, depthmask]) => ({
        privilegeid,
        depthmask,
      }));
      await http.patch(`/admin/platform-roles/${detail.roleid}/privileges`, {
        changes,
        appName: 'admin',
      });
      message.success(t('privilegesSaved'));
      setPrivilegeChanges(new Map());
      await loadPrivileges();
      onSaved?.();
    } catch {
      message.error(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const drawerExtra =
    activeTab === 'apps' ? (
      <Button type="primary" icon={<Save size={14} />} loading={saving} onClick={saveApps}>
        {t('save')}
      </Button>
    ) : activeTab === 'menu' ? (
      <Button type="primary" icon={<Save size={14} />} loading={saving} onClick={saveAdminMenus}>
        {t('save')}
      </Button>
    ) : activeTab === 'privileges' ? (
      <Button
        type="primary"
        icon={<Save size={14} />}
        loading={saving}
        disabled={privilegeChanges.size === 0}
        onClick={savePlatformPrivileges}
      >
        {t('savePrivilegeChanges', { count: privilegeChanges.size })}
      </Button>
    ) : null;

  return (
    <Drawer
      open={!!roleId}
      onClose={onClose}
      width={720}
      destroyOnClose
      title={
        detail ? (
          <Space size={8}>
            <ShieldCheck size={18} color="#0078d4" />
            <Typography.Text strong style={{ fontSize: 16 }}>
              {detail.name}
            </Typography.Text>
            <Tag color={platformRoleTagColor(detail.roleType)}>
              {isPlatformRoleType(detail.roleType) ? t(detail.roleType) : detail.roleType}
            </Tag>
            <Tag color="blue">
              {detail.memberCount} {t('members')}
            </Tag>
          </Space>
        ) : (
          t('editRole')
        )
      }
      extra={drawerExtra}
    >
      {loading || !detail ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin />
        </div>
      ) : (
        <PlatformPermissionDrawerTabs
          activeTab={activeTab}
          detail={detail}
          apps={sitemap?.apps ?? []}
          selectedAppIds={selectedAppIds}
          adminTree={adminTree}
          checkedAdminIds={checkedAdminIds}
          privilegeData={privilegeData}
          privilegeLoading={privilegeLoading}
          privilegeChanges={privilegeChanges}
          privilegeQ={privilegeQ}
          privilegePage={privilegePage}
          privilegePageSize={privilegePageSize}
          onTabChange={setActiveTab}
          onSelectedAppIdsChange={setSelectedAppIds}
          onCheckedAdminIdsChange={setCheckedAdminIds}
          onPrivilegeChanges={setPrivilegeChanges}
          onPrivilegeQChange={(nextQ) => {
            setPrivilegeQ(nextQ);
            setPrivilegePage(1);
          }}
          onPrivilegePageChange={(nextPage, nextPageSize) => {
            setPrivilegePage(nextPage);
            setPrivilegePageSize(nextPageSize);
          }}
          onMembersChanged={refreshDetail}
        />
      )}
    </Drawer>
  );
}
